import { useEffect, useState } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import Snackbar from '@mui/material/Snackbar'
import Checkbox from '@mui/material/Checkbox'
import Switch from '@mui/material/Switch'
import Alert from '@mui/material/Alert'
import CloseIcon from '@mui/icons-material/Close'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import Button from '@mui/material/Button'
import FormControlLabel from '@mui/material/FormControlLabel'
import TextField from '@mui/material/TextField'

import { useTranslation } from 'react-i18next'
import {
  useGetBuildingsQuery,
  useGetTypesBuildingsQuery,
  useUpdateBuildingMutation
} from '@/api'
import {
  useGetOpenReasonSettingQuery,
  useUpdateOpenReasonSettingMutation
} from '@/api/adminServiceApi'
import {
  getBuildingObjectCode,
  isValidBuildingObjectCode,
  sanitizeBuildingObjectCodeInput
} from '@/utils/buildings'

const UpdateModal = ({
  open = false,
  onClose = () => {},
  currentId = null,
  canEditActive = true 
}) => {
  const { t } = useTranslation()
  const { data: buildingTypes } = useGetTypesBuildingsQuery()
  const { data: building } = useGetBuildingsQuery(undefined, {
    selectFromResult: (result) => ({
      data: result.data?.find((item) => item.id === currentId)
    })
  })
  const [updateBuilding, { isSuccess: updateSuccess, isLoading }] =
    useUpdateBuildingMutation()
  const { data: openReasonSetting } = useGetOpenReasonSettingQuery(currentId, {
    skip: !open || !currentId
  })
  const [updateOpenReasonSetting] = useUpdateOpenReasonSettingMutation()
  const [form, setForm] = useState({
    objectCode: '',
    isExit: false,
    buildingTypeId: building?.building_type_id || 0,
    isActive: true,
    requireOpenReason: false
  })
  const [showError, setShowError] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const objectCodeError =
    form.objectCode.length > 0 && !isValidBuildingObjectCode(form.objectCode)

  const handleChange = ({ target }) => {
    if (target.name === 'objectCode') {
      setForm({
        ...form,
        objectCode: sanitizeBuildingObjectCodeInput(target.value)
      })
    } else if (target.name === 'isExit') {
      setForm({ ...form, isExit: !form.isExit })
    } else if (target.name === 'isActive') {
      setForm({ ...form, isActive: target.checked })
    } else if (target.name === 'requireOpenReason') {
      setForm({ ...form, requireOpenReason: target.checked })
    } else {
      setForm({
        ...form,
        [target.name]: target.value
      })
    }
  }

  const onShowError = () => {
    setShowError(true)
  }

  const onCloseError = (event, reason) => {
    if (reason === 'clickaway') {
      return
    }
    setShowError(false)
  }

  const onCloseSuccess = (event, reason) => {
    if (reason === 'clickaway') {
      return
    }
    setShowSuccess(false)
  }

  const onShowSuccess = () => {
    setShowSuccess(true)
  }

  const handleSubmit = async () => {
    try {
      await updateBuilding({
        id: building?.id,
        ...(isValidBuildingObjectCode(form.objectCode) && {
          object_code: form.objectCode
        }),
        building_type_id: +form.buildingTypeId,
        exit_if_not_found: form.isExit,
        is_active: form.isActive
      }).unwrap()
      if (currentId) {
        await updateOpenReasonSetting({
          buildingId: currentId,
          requireOpenReason: form.requireOpenReason
        }).unwrap()
      }
      onShowSuccess()
      onClose()
    } catch (_err) {
      onShowError()
    }
  }

  useEffect(() => {
    if (currentId && building) {
      setForm({
        objectCode: getBuildingObjectCode(building),
        isExit: building?.exit_if_not_found || false,
        buildingTypeId: building?.building_type_id || 0,
        isActive: building?.is_active ?? true,
        requireOpenReason: openReasonSetting?.require_open_reason || false
      })
    }

    return () => {
      setForm({
        objectCode: '',
        isExit: false,
        buildingTypeId: null,
        isActive: true,
        requireOpenReason: false
      })
    }
  }, [
    building,
    building?.building_type_id,
    building?.exit_if_not_found,
    building?.is_active,
    openReasonSetting?.require_open_reason,
    currentId
  ])

  return (
    <>
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>
          {t('editing_an_object')}
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500]
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            margin="normal"
            label={t('object_code')}
            name="objectCode"
            value={form.objectCode}
            onChange={handleChange}
            error={objectCodeError}
            helperText={
              objectCodeError
                ? t('object_code_required_format')
                : t('object_code_helper')
            }
            slotProps={{
              htmlInput: {
                inputMode: 'numeric',
                pattern: '[0-9]*',
                maxLength: 4
              }
            }}
          />
          <FormControl fullWidth sx={{ mt: 3 }}>
            <InputLabel>{t('operating_mode')}</InputLabel>
            <Select
              labelId="buildingTypeId"
              id="buildingTypeId"
              value={form.buildingTypeId || ''}
              label="Выберите статус"
              name="buildingTypeId"
              onChange={handleChange}
            >
              {buildingTypes?.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={form.isExit}
                onChange={handleChange}
                name="isExit"
              />
            }
            label={t('open_at_the_exit_if_the_car_is_not_on_the_list')}
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.isActive}
                onChange={handleChange}
                name="isActive"
                disabled={!canEditActive}
              />
            }
            label={t('active')}
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.requireOpenReason}
                onChange={handleChange}
                name="requireOpenReason"
              />
            }
            label={t('require_open_reason')}
          />

          <Button
            sx={{ mt: 3 }}
            variant="contained"
            fullWidth
            loading={isLoading}
            onClick={handleSubmit}
            disabled={objectCodeError}
          >
            {t('save')}
          </Button>
        </DialogContent>
      </Dialog>
      <Snackbar open={showError} autoHideDuration={6000} onClose={onCloseError}>
        <Alert
          sx={{ width: '100%' }}
          severity="error"
          variant="filled"
          onClose={onCloseError}
        >
          {t('theres_been_an_error_please_try_again_later')}
        </Alert>
      </Snackbar>
      <Snackbar
        open={updateSuccess && showSuccess}
        autoHideDuration={3000}
        onClose={onCloseSuccess}
      >
        <Alert
          sx={{ width: '100%' }}
          severity="success"
          variant="filled"
          onClose={onCloseSuccess}
        >
          {t('the_changes_have_been_saved')}
        </Alert>
      </Snackbar>
    </>
  )
}

// UpdateModal.propTypes = {
//   open: PropTypes.bool,
//   onClose: PropTypes.func,
//   currentId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
//   canEditActive: PropTypes.bool
// }

export default UpdateModal
